import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, OnInit, Optional } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faCheck, faFlask, faLayerGroup, faPlus, faSliders, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { forkJoin } from 'rxjs';
import * as _ from 'underscore';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { ConfirmationDialogComponent } from '../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { InventoryService } from '../../../pick-plan/services/inventory.service';
import { InventoryItemDTO } from '../../models/item';
import { InventoryItemAssemblyAttributeDTO, InventoryItemAssemblyAttributeOptionDTO, InventoryItemAssemblyAttributeValueDTO, InventoryItemAssemblyComponentDTO, InventoryItemAssemblyDTO } from '../../models/item-assembly';

export interface ItemAssemblyPopupData {
  itemAssembly?: InventoryItemAssemblyDTO;
  /** Pre-selects the created product (media creation work orders). */
  createItem?: number;
}

/** Creates or edits a media assembly and its constituents. Closes with true when the caller should reload. */
@Component({
  selector: 'item-assembly-edit',
  templateUrl: './item-assembly-edit.component.html',
  styleUrls: ['./item-assembly-edit.component.scss']
})
export class ItemAssemblyEditComponent implements OnInit {

  static open(dialog: MatDialog, data: ItemAssemblyPopupData): MatDialogRef<ItemAssemblyEditComponent, boolean> {
    return dialog.open(ItemAssemblyEditComponent, { data, width: '860px', maxWidth: '95vw' });
  }

  public faCheck = faCheck;
  public faFlask = faFlask;
  public faLayerGroup = faLayerGroup;
  public faPlus = faPlus;
  public faSliders = faSliders;
  public faTrash = faTrash;
  public faXmark = faXmark;

  public assembly: InventoryItemAssemblyDTO;
  public isLoading = true;
  public items: InventoryItemDTO[] = [];
  public constituentItems: InventoryItemDTO[] = [];
  public assemblyAttributes: InventoryItemAssemblyAttributeDTO[] = [];
  public currentComponent: InventoryItemAssemblyComponentDTO = {};
  /** Removing a saved component deletes it right away, so the caller must reload even on cancel. */
  private changed = false;
  /** The module provides a stub MatDialogRef, so dialog data is the only reliable sign we're in a dialog. */
  private readonly isDialog: boolean;

  private destroyRef = inject(DestroyRef);

  // Optional: the legacy item-assembly-edit route renders this without a dialog.
  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ItemAssemblyPopupData,
    private dialogRef: MatDialogRef<ItemAssemblyEditComponent, boolean>,
    private inventoryService: InventoryService,
    private location: Location,
    private changeDetRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private errorService: ErrorService,
  ) {
    this.isDialog = !!data;
    this.data = data ?? {};
    // A copy, so cancelling leaves the list's row untouched.
    this.assembly = {
      ...this.data.itemAssembly,
      components: [...(this.data.itemAssembly?.components ?? [])],
      attributeValues: (this.data.itemAssembly?.attributeValues ?? []).map(value => ({ ...value }))
    };
    if (this.data.createItem)
      this.assembly.itemId = this.data.createItem;
  }

  ngOnInit() {
    forkJoin([
      this.inventoryService.getInventoryItems(),
      this.inventoryService.getInventoryItemAssemblyAttributes()
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([items, attributes]) => {
        this.constituentItems = _.sortBy((items ?? []).filter(item => item.parentItemName == 'Plant Media Constituent'), item => item.name);
        this.items = _.sortBy((items ?? []).filter(item => item.parentItemName == 'Plant Media'), item => item.name);
        this.assemblyAttributes = attributes ?? [];
        this.customFields();
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  get canAddComponent(): boolean {
    return !!this.currentComponent.item;
  }

  submit() {
    this.isLoading = true;
    const request = this.assembly.id
      ? this.inventoryService.putInventoryItemAssembly(this.assembly)
      : this.inventoryService.postInventoryItemAssembly(this.assembly);

    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.close(true),
      error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  cancel() {
    this.close(this.changed);
  }

  addComponent() {
    if (!this.canAddComponent) return;

    const { isRequired, item, notes, quantityRequired, unitQuantity } = this.currentComponent;
    this.assembly.components = [...this.assembly.components, { isRequired, item, itemId: item.id, notes, quantityRequired, unitQuantity }];
    this.currentComponent = {};
  }

  removeComponent(component: InventoryItemAssemblyComponentDTO) {
    if (!component.id) {
      this.assembly.components = this.assembly.components.filter(x => x !== component);
      return;
    }

    this.isLoading = true;
    this.inventoryService.deleteInventoryItemAssemblyComponent(component.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.assembly.components = this.assembly.components.filter(x => x !== component);
        this.changed = true;
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  deleteAssembly() {
    this.dialog.open(ConfirmationDialogComponent, {
      minWidth: '10%',
      data: {
        confirmLabel: 'Yes',
        title: 'Delete Assembly?',
        confirmMessage: `Are you sure you want to delete ${this.assembly.name || 'this assembly'}?`,
        cancelLabel: 'No'
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (confirmed !== true) return;

      this.isLoading = true;
      this.inventoryService.deleteInventoryItemAssembly(this.assembly.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => this.close(true),
        error: err => {
          this.errorService.handleError(err);
          this.isLoading = false;
        }
      });
    });
  }

  getFieldOptions(field: InventoryItemAssemblyAttributeValueDTO): InventoryItemAssemblyAttributeOptionDTO[] {
    return this.attributeFor(field)?.options ?? [];
  }

  trackByComponent(index: number, component: InventoryItemAssemblyComponentDTO): number {
    return component.id ?? -index - 1;
  }

  private attributeFor(field: InventoryItemAssemblyAttributeValueDTO): InventoryItemAssemblyAttributeDTO | undefined {
    return this.assemblyAttributes.find(x => x.id == field.inventoryItemAssemblyAttributeId);
  }

  /** Adds an empty value for every attribute the assembly doesn't have yet. */
  private customFields() {
    this.assemblyAttributes.forEach(attribute => {
      const existing = this.assembly.attributeValues.find(x => x.inventoryItemAssemblyAttributeId == attribute.id);
      if (existing) {
        existing.description = attribute.description;
        existing.name = attribute.name;
        existing.id = this.assembly.id;
      } else {
        this.assembly.attributeValues.push({
          datatype: attribute.datatype,
          description: attribute.description,
          name: attribute.name,
          id: 0,
          inventoryItemAssemblyAttributeId: attribute.id,
          isActive: true
        });
      }
    });
  }

  private close(saved: boolean) {
    if (this.isDialog)
      this.dialogRef.close(saved);
    else
      this.location.back();
  }
}
